import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import BaziCard from '../../components/BaziCard';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import TagManageModal from '../../components/TagManageModal';
import { EmptyState, SkeletonGrid } from '../../components/ListStates';
import { baziAPI } from '../../api/api';
import { comparePinyin } from '../../utils/pinyin';
import { Search, Plus, Tags, Trash2 } from 'lucide-react';
import './Dashboard.css';

const PAGE_SIZE = 24;

const Dashboard = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('recent'); // recent | pinyin
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [tagTarget, setTagTarget] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await baziAPI.getAll();
      setRecords(response.data.data);
      setError('');
    } catch (err) {
      setError('加载失败，请重试');
      console.error('获取记录失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = records;
    if (q) list = list.filter((r) => (r.name || '').toLowerCase().includes(q));
    const sorted = [...list];
    if (sort === 'pinyin') {
      sorted.sort((a, b) => comparePinyin(a.name, b.name));
    } else {
      sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }
    return sorted;
  }, [records, search, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const pageRecords = filtered.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);

  // 搜索/排序变化时回到第一页
  useEffect(() => { setPage(1); }, [search, sort]);

  const changePage = (p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const confirmDelete = async () => {
    const id = deleteTarget?._id;
    if (!id) return;
    try {
      await baziAPI.delete(id);
      setRecords((prev) => prev.filter((r) => r._id !== id));
    } catch (err) {
      console.error('删除记录失败:', err);
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleTagsSaved = (updated) => {
    if (!updated) return;
    setRecords((prev) => prev.map((r) => (r._id === updated._id ? { ...r, labels: updated.labels } : r)));
  };

  return (
    <div className="dashboard">
      <Navbar />
      <div className="container">
        <div className="dashboard-toolbar">
          <div className="dashboard-toolbar-left">
            <h1>我的八字</h1>
            {!loading && !error && <span className="dashboard-count">共 {filtered.length} 例</span>}
          </div>
          <div className="dashboard-toolbar-right">
            <div className="dashboard-search">
              <Search size={16} className="dashboard-search-icon" />
              <input
                type="text"
                placeholder="搜索姓名…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select className="dashboard-sort" value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="recent">最近添加</option>
              <option value="pinyin">姓名拼音</option>
            </select>
            <button className="btn btn-primary dashboard-new" onClick={() => navigate('/bazi/new')}>
              <Plus size={16} className="inline-icon" /> 新建排盘
            </button>
          </div>
        </div>

        {loading ? (
          <SkeletonGrid count={8} className="records-grid" />
        ) : error ? (
          <div className="list-empty">
            <p className="list-empty-text">{error}</p>
            <button className="btn btn-secondary" onClick={fetchRecords}>重试</button>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            message={search ? '没有找到匹配的命例' : '还没有任何八字记录'}
            actionLabel={search ? undefined : '创建第一个排盘'}
            onAction={() => navigate('/bazi/new')}
          />
        ) : (
          <>
            <div className="records-grid">
              {pageRecords.map((record) => (
                <BaziCard
                  key={record._id}
                  record={record}
                  onOpen={(id) => navigate(`/bazi/${id}`)}
                  actions={[
                    { key: 'tags', label: '标签管理', icon: <Tags size={15} />, onClick: () => setTagTarget(record) },
                    { key: 'del', label: '删除', icon: <Trash2 size={15} />, danger: true, onClick: () => setDeleteTarget(record) }
                  ]}
                />
              ))}
            </div>
            <Pagination page={curPage} totalPages={totalPages} onChange={changePage} />
          </>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        danger
        title="删除命例"
        message={<>确定删除命例「<b>{deleteTarget?.name}</b>」吗？此操作不可撤销。</>}
        confirmText="删除"
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <TagManageModal
        open={!!tagTarget}
        record={tagTarget}
        onClose={() => setTagTarget(null)}
        onSaved={handleTagsSaved}
      />
    </div>
  );
};

export default Dashboard;
